import { useCallback, useEffect, useState } from 'react';
import { getBundles } from './api';
import { BUNDLE_KEYS, defaultFeaturesForBundles, orderFeatureKeys } from './featureAccess';

// Live bundle → features map, read from the backend.
//
// A bundle's real feature set is the `bundle_features` DB override when a
// gohire-admin has customized it, else the config default. Anything that shows
// "what does this bundle grant" MUST resolve through here — resolving from the
// static BUNDLES catalog reports the defaults and silently ignores every
// customization (which is exactly the bug this hook exists to fix).
//
// Cached module-wide: the catalog is global and small, and several components
// (Licenses card, per-user Access modal) want it at once. Callers get the
// config defaults until the fetch lands, so chips render immediately and then
// correct themselves rather than flashing empty.

let cache = null; // bundleKey -> [featureKey]
let inflight = null;

function fetchMap() {
    if (cache) return Promise.resolve(cache);
    if (!inflight) {
        inflight = getBundles()
            .then((res) => {
                const list = (res && res.data && res.data.bundles) || [];
                const map = {};
                list.forEach((b) => { map[b.key] = b.features || []; });
                cache = map;
                return map;
            })
            .catch(() => null)
            .finally(() => { inflight = null; });
    }
    return inflight;
}

// Drop the cache so the next read re-fetches — call after saving a bundle edit.
export function invalidateBundleFeatures() {
    cache = null;
}

export default function useBundleFeatures() {
    const [map, setMap] = useState(cache);

    useEffect(() => {
        let cancelled = false;
        fetchMap().then((m) => { if (!cancelled && m) setMap(m); });
        return () => { cancelled = true; };
    }, []);

    // Union of the features granted by a set of bundle keys. Stable across
    // renders (it lands in useMemo dep arrays at the call sites).
    const featuresForBundles = useCallback((bundleKeys) => {
        if (!map) return defaultFeaturesForBundles(bundleKeys);
        const set = new Set();
        (bundleKeys || []).forEach((k) => (map[k] || []).forEach((f) => set.add(f)));
        return orderFeatureKeys(set);
    }, [map]);

    // A single bundle's features (empty for an unknown key).
    const featuresForBundle = useCallback((bundleKey) => (
        map ? (map[bundleKey] || []) : defaultFeaturesForBundles([bundleKey])
    ), [map]);

    return {
        loaded: !!map,
        bundleKeys: BUNDLE_KEYS,
        featuresForBundles,
        featuresForBundle,
    };
}
